import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FaCheck } from "react-icons/fa";
import { FoaPart, NoticeRoute, type SourcingEventDto, type SourcingEventPatchDto } from "../../../../../autogen/bff-api";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { FieldSaveErrorMessage, useFieldSaveError } from "./FieldSaveError";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { isNegotiationWithoutNotice } from "../utils/negotiationWithoutNotice";

const NOR = "NOR";
const ENG = "ENG";

const isTedBound = (foaPart: FoaPart | null | undefined): boolean =>
  foaPart === FoaPart.PartIii || foaPart === FoaPart.PartIv;

interface Props {
  eventId: string;
}

export const SubmissionLanguagesField = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;

  const selected = publicContext?.eFormsNoticeData?.submissionLanguages ?? [];
  const withoutNotice = isNegotiationWithoutNotice(publicContext?.procedureType);
  const tedBound =
    (isTedBound(publicContext?.foaPart) || state.contractNoticeRoute === NoticeRoute.Eu) && !withoutNotice;
  const norOnlyOnTed = tedBound && selected.length === 1 && selected.includes(NOR);

  const saving = fieldStatus("publicContext.eFormsNoticeData.submissionLanguages");
  const isSaving = saving === "saving" || saving === "pending";
  const saveError = useFieldSaveError(eventId, "publicContext.eFormsNoticeData.submissionLanguages");

  const isLastSelected = (code: string) => selected.length === 1 && selected.includes(code);

  const toggle = (code: string) => {
    if (isLastSelected(code)) return;
    const next = selected.includes(code)
      ? selected.filter((c) => c !== code)
      : [...selected, code];
    updateField({
      publicContext: { eFormsNoticeData: { submissionLanguages: next } },
    });
  };

  const helpText = tedBound
    ? norOnlyOnTed
      ? t(
          "The notice goes to TED. You have chosen to accept bids in Norwegian only — that is permitted, but it can limit competition from EEA suppliers.",
        )
      : t(
          "English is suggested because this notice will be published to TED and open for EEA-wide competition. Deselect it if you only want Norwegian bids.",
        )
    : withoutNotice
      ? t("No notice is published, so choose the languages the invited suppliers may tender in.")
      : t("Norwegian is the default for national procurements.");

  return (
    <FormControl data-testid="submission-languages" isInvalid={!!saveError}>
      <Flex align="center" gap="2" mb="2">
        <FormLabel mb="0">{t("Submission language")}</FormLabel>
        {isSaving && <Spinner size="xs" />}
      </Flex>
      <Flex gap="3">
        <LanguageChip
          label={t("Norwegian")}
          selected={selected.includes(NOR)}
          isLastSelected={isLastSelected(NOR)}
          onClick={() => toggle(NOR)}
        />
        <LanguageChip
          label={t("English")}
          selected={selected.includes(ENG)}
          isLastSelected={isLastSelected(ENG)}
          onClick={() => toggle(ENG)}
        />
      </Flex>
      <FieldSaveErrorMessage error={saveError} />
      <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
        <AlertIcon />
        <Text>{helpText}</Text>
      </Alert>
    </FormControl>
  );
};

interface ChipProps {
  label: string;
  selected: boolean;
  isLastSelected: boolean;
  onClick: () => void;
}

const LanguageChip = ({ label, selected, isLastSelected, onClick }: ChipProps) => {
  const { t } = useTranslation();
  return (
  <SelectableCard
    isSelected={selected}
    isDisabled={isLastSelected}
    disabledLabel={isLastSelected ? t("Required") ?? "Required" : undefined}
    onClick={onClick}
    aria-pressed={selected}
    aria-disabled={isLastSelected}
    px="4"
    py="2"
    borderRadius="md"
    minW="120px"
  >
    <Flex align="center" gap="2" justify="center">
      <Box
        w="16px"
        h="16px"
        borderRadius="sm"
        border="1.5px solid"
        borderColor={selected ? "smPrimary" : "smBorder"}
        bg={selected ? "smPrimary" : "transparent"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {selected && <Icon as={FaCheck} boxSize="2.5" color="white" />}
      </Box>
      <Text fontWeight="medium" fontSize="sm" color={selected ? "smText" : "smMuted"}>
        {label}
      </Text>
    </Flex>
  </SelectableCard>
  );
};
